import cx from 'classnames';
import dynamic from 'next/dynamic';
import TrackWrapper from '@/common/TrackWrapper';
import Sections from '~/constants/home/sections';
import { useIsMobile, useIsNative } from '~/context/common/DeviceContext';
import { useConsumer } from '~/context/common/StaticContext';
import { useGetUser, useIsLoggedIn } from '~/context/common/UserContext';
import { useSavedSearches } from '~/context/home/SavedSearchesContext';
import useTrackHeroPerformace from '~/hooks/home/useTrackHeroPerformace';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import { Copy, HeroImage, HeroWrapper, SubCopy } from './Hero.styles';
import HeroCardsReturnUser from '../HeroCardsReturnUser/HeroCardsReturnUser';
import useIsInReturnUserExperiment from '../hooks/useIsInReturnUserExperiment';

const MerchSearchBar = dynamic(
  () =>
    import(
      /* webpackChunkName: "MerchSearchBar" */ '@/home/SearchBar/SearchBar'
    )
);

const SearchBar = dynamic(
  () => import(/* webpackChunkName: "SearchBar" */ '@/common/SearchBar')
);

const cdnUrl = process.env.CVNA_APP_CDN_URL;
const assetsUrl = process.env.CVNA_APP_CDN_ASSETS_PATH;

type OwnProps = {
  isReturningUser: boolean;
};

function Hero({ isReturningUser }: OwnProps) {
  const savedSearches = useSavedSearches();
  const consumer = useConsumer();
  const user = useGetUser();
  const isMobile = useIsMobile();
  const isLoggedIn = useIsLoggedIn();
  useTrackHeroPerformace();

  const isNative = useIsNative();
  const shouldRenderMerchSearchBar = !isNative;
  const isInReturnUserExperiment = useIsInReturnUserExperiment();
  const shouldShowHomeReturnUserExperiment =
    isInReturnUserExperiment && isReturningUser;

  const heroImg = {
    large: `${cdnUrl}/home-assets/redesign/hero_102523.jpeg?width=1800`,
    small: `${cdnUrl}/home-assets/redesign/hero_102523.jpeg?&width=800`
  };
  const returnUserHeroImg = {
    desktop: `${assetsUrl}/images/blue_hero_halo_background_desktop.svg`,
    mobile: `${assetsUrl}/images/blue_hero_halo_background_mobile.svg`
  };

  const userNameString =
    isInReturnUserExperiment && user.given_name
      ? `,${isMobile ? '\n' : ' '}${user.given_name}`
      : '';
  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes: {
      description: 'Hero'
    }
  };

  const copySectionClasses = cx(
    'flex flex-col items-center',
    {
      'absolute left-1/2 top-[59px]  w-fit -translate-x-1/2  m:top-[114px]':
        !isReturningUser
    },
    {
      'mx-auto h-[235px] w-auto bg-grey-1 px-24 py-40 m:h-[240px] m:pb-[44px] m:pt-[40px]':
        isReturningUser && isInReturnUserExperiment
    },
    {
      'mx-auto h-[182px] w-auto bg-grey-1 px-24 py-40 m:h-[235px] m:pb-[44px] m:pt-[55px]':
        isReturningUser && !isInReturnUserExperiment
    }
  );

  const searchBarClasses = cx(
    'absolute left-1/2 top-0 z-[2] mt-16 h-56 w-full max-w-[336px] -translate-x-1/2 m:mt-24 m:max-w-[680px]',
    { 'top-[45%] m:top-[52%]': !isReturningUser },
    {
      'top-[40%] m:top-[47%]': isReturningUser && !isInReturnUserExperiment
    },
    {
      'top-[43%] m:top-[34%]':
        isReturningUser && isInReturnUserExperiment && isLoggedIn
    },
    {
      'top-[30%] m:top-[34%]':
        isReturningUser && isInReturnUserExperiment && !isLoggedIn
    }
  );

  return (
    <>
      <div className="relative">
        <TrackWrapper event={viewEvent} trackOnce>
          <HeroWrapper>
            {!isReturningUser && (
              <HeroImage
                id="redesign-hero-image"
                style={{
                  backgroundImage: isMobile
                    ? `url(${heroImg.small})`
                    : `url(${heroImg.large})`
                }}
              />
            )}

            <section
              className={copySectionClasses}
              style={{
                background: shouldShowHomeReturnUserExperiment
                  ? isMobile
                    ? `url(${returnUserHeroImg.mobile}) no-repeat center / cover #228BE6`
                    : `url(${returnUserHeroImg.desktop}) no-repeat center / cover #228BE6`
                  : 'none',
                backgroundSize: 'cover'
              }}
            >
              <Copy
                className={cx(
                  isReturningUser && !isInReturnUserExperiment
                    ? 'text-blue-6'
                    : 'text-white',
                  isReturningUser && isInReturnUserExperiment
                    ? 'whitespace-pre-line text-center text-[32px] !font-black uppercase leading-8 m:tl-brand-m'
                    : 'tl-brand-s m:tl-brand-l'
                )}
                data-qa="hero-title"
              >
                {isReturningUser
                  ? `welcome back${userNameString}`
                  : 'right car. right price.'}
              </Copy>

              {!isReturningUser && <SubCopy>Financing made easy.</SubCopy>}
            </section>
          </HeroWrapper>
        </TrackWrapper>

        <TrackWrapper event={{ eventName: 'search_bar_viewed' }}>
          <div className={searchBarClasses}>
            {shouldRenderMerchSearchBar ? (
              <MerchSearchBar />
            ) : (
              <SearchBar
                background="white"
                button="large"
                consumer={consumer}
                savedSearches={savedSearches}
              />
            )}
          </div>
        </TrackWrapper>
      </div>

      {shouldShowHomeReturnUserExperiment && <HeroCardsReturnUser />}
    </>
  );
}

export default withErrorBoundary(Hero, undefined, undefined, Sections.Hero);
